import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { decimalsFor } from '@/lib/money';
import { bangkokShortDateTime, normalizeTimestamp } from '@/lib/datetime.js';

/**
 * Render a sortable-looking header. Real sorting hook-in is left to the
 * caller (columns can wire `column.getIsSorted()` with react-table state).
 */
export function createSortableHeader(label: string) {
  return function SortableHeader() {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
      </button>
    );
  };
}

export type ActionTone = 'default' | 'destructive' | 'success' | 'warning';

const ACTION_TONE_CLASSES: Record<ActionTone, string> = {
  default: '',
  destructive: 'text-destructive focus:text-destructive',
  success: 'text-emerald-600 focus:text-emerald-600 dark:text-emerald-400',
  warning: 'text-amber-600 focus:text-amber-600 dark:text-amber-400',
};

export interface RowAction<TRow> {
  label: React.ReactNode | ((row: TRow) => React.ReactNode);
  onClick: (row: TRow) => void;
  /** Red text. Kept for back-compat; equivalent to `tone: 'destructive'`. */
  destructive?: boolean;
  /** Text colour of the item; may vary per row (e.g. suspend vs enable). */
  tone?: ActionTone | ((row: TRow) => ActionTone);
  disabled?: (row: TRow) => boolean;
  /** When it returns true for a row, the item is not rendered for that row. */
  hidden?: (row: TRow) => boolean;
  /**
   * Why this item is greyed out, shown on hover. An action a row cannot use is
   * otherwise a dead entry that says nothing about who could use it or what
   * would have to change.
   */
  titleFor?: (row: TRow) => string | undefined;
}

export interface ActionsCellOptions<TRow> {
  /** The trigger's accessible name for this row; name the row so each menu is told apart. Defaults to "Open menu". */
  ariaLabel?: (row: TRow) => string;
}

export function createActionsCell<TRow>(actions: RowAction<TRow>[], options: ActionsCellOptions<TRow> = {}) {
  return function ActionsCell({ row }: { row: { original: TRow } }) {
    const { t } = useTranslation();
    const r = row.original;
    return (
      // modal={false}: a modal dropdown locks body pointer-events; when an
      // item opens a Dialog/Sheet the two locks stack and the body stays
      // `pointer-events: none` after the overlay closes, freezing the page.
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" aria-hidden />
            <span className="sr-only">{options.ariaLabel?.(r) ?? t('common.openMenu')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {actions.filter((a) => !a.hidden?.(r)).map((a, i) => {
            const tone: ActionTone =
              typeof a.tone === 'function'
                ? a.tone(r)
                : a.tone ?? (a.destructive ? 'destructive' : 'default');
            const label = typeof a.label === 'function' ? a.label(r) : a.label;
            const reason = a.titleFor?.(r);
            return (
              <DropdownMenuItem
                key={i}
                disabled={a.disabled?.(r)}
                onClick={(e) => {
                  e.preventDefault();
                  a.onClick(r);
                }}
                className={cn(ACTION_TONE_CLASSES[tone])}
              >
                {reason ? (
                  <TooltipProvider>
                    <Tooltip>
                      {/* `pointer-events-auto` on the trigger: a disabled menu
                          item carries `pointer-events: none`, so without it the
                          explanation for the greying-out is the one thing on the
                          row that cannot be hovered. */}
                      <TooltipTrigger asChild>
                        <span className="pointer-events-auto" title={reason}>
                          {label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{reason}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  label
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };
}

/**
 * Format a money amount stored as minor units (satang/cents) into a currency
 * string with the native symbol (฿/$/¥…), grouped thousands, and per-currency
 * precision. The minor-unit exponent comes from `decimalsFor` (CLAUDE.md §2.4),
 * so JPY/KRW (0dp) and the Gulf dinars (3dp) are not corrupted by a hardcoded
 * /100 — and `narrowSymbol` keeps this consistent with `moneyHint`/`signedMoney`
 * (฿, never the "THB" ISO code).
 */
export function formatMoney(
  minor: number | string | null | undefined,
  opts: { currency?: string; locale?: string } = {},
): string {
  if (minor === null || minor === undefined) return '-';
  const asNumber = typeof minor === 'string' ? Number(minor) : minor;
  if (!Number.isFinite(asNumber)) return '-';
  const { currency = 'THB', locale = 'en-US' } = opts;
  const decimals = decimalsFor(currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(asNumber / 10 ** decimals);
}

/**
 * Money for a row whose agent currency is resolved per row by
 * `useAgentCurrencyMap`, which answers no currency in three cases: `undefined`
 * while the org list is in flight, after it fails, or for an id outside the list
 * that came back, and `null` when the row was read and records none.
 *
 * Two things this stops. `formatMoney`'s `currency` default only fires on
 * `undefined`, so handing it that `null` reaches `Intl.NumberFormat` and throws
 * `RangeError: Invalid currency code` — the whole table, not one cell. And
 * papering over it with `?? 'THB'` would put back exactly the silent default
 * these call sites exist to remove: a JPY row rendered as baht understates it
 * 100x with nothing on screen to say so. Unknown renders as unknown.
 */
export function formatMoneyIn(
  minor: number | string | null | undefined,
  currency: string | null | undefined,
  opts: { locale?: string } = {},
): string {
  if (!currency) return '-';
  return formatMoney(minor, { ...opts, currency });
}

// reserve_credit stores the master-agent loss carry-forward as a value <= 0
// (svc-commission settlement engine); the UI shows its magnitude, never a sign.
export function formatReserve(
  minor: number | string | null | undefined,
  opts: { currency?: string; locale?: string } = {},
): string {
  const asNumber = typeof minor === 'string' ? Number(minor) : minor;
  if (asNumber === null || asNumber === undefined || !Number.isFinite(asNumber)) {
    return formatMoney(minor, opts);
  }
  return formatMoney(Math.abs(asNumber), opts);
}

// Lives in `lib/datetime` alongside the Bangkok formatters that need it too;
// re-exported here because the table cells have always imported it from this
// module.
export { normalizeTimestamp };

/**
 * Format an ISO date string (or Date) in a compact form: the Bangkok instant,
 * with month names in `opts.locale`.
 *
 * This is the back office's workhorse timestamp — the great majority of table
 * cells go through it — so the zone it renders in is the zone the product is
 * read in. It is pinned to Bangkok in `lib/datetime`, not chosen here; the
 * signature is kept so the call sites stay untouched.
 */
export function formatDate(
  iso: string | Date | null | undefined,
  opts: { locale?: string; withTime?: boolean } = {},
): string {
  return bangkokShortDateTime(iso, { lang: opts.locale, withTime: opts.withTime });
}

/**
 * Render a resolved username with the full id available on hover; while the
 * lookup is pending (or the id is unresolvable) fall back to a truncated
 * monospace id so the cell is never blank.
 */
export function nameCell(
  name: string | null | undefined,
  id: string | null | undefined,
) {
  if (!id) return <span className="text-muted-foreground">-</span>;
  if (name) return <span title={id}>{name}</span>;
  return (
    <span className="font-mono text-xs" title={id}>
      {id.slice(0, 8)}
    </span>
  );
}

export type StatusTone =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'muted';

const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  default: 'bg-muted text-foreground',
  success: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  warning: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  danger: 'bg-destructive/15 text-destructive',
  info: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  muted: 'bg-muted/50 text-muted-foreground',
};

export function statusBadge(label: React.ReactNode, tone: StatusTone = 'default') {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        STATUS_TONE_CLASSES[tone],
      )}
    >
      {label}
    </span>
  );
}
